import { cva, type VariantProps } from 'class-variance-authority'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

// Re-export VariantProps for use in components
export type { VariantProps }

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/* ========================================
   BUTTON VARIANTS
   ======================================== */

export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-lg font-medium text-body-small",
    "transition-smooth",
    "focus-ring",
    "disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
    "whitespace-nowrap select-none"
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-brand-600 !text-white hover:bg-brand-700 focus:bg-brand-700",
          "shadow-sm hover:shadow-md",
          "border border-transparent"
        ],
        destructive: [
          "bg-red-500 !text-white hover:bg-red-600",
          "shadow-sm hover:shadow-md"
        ],
        outline: [
          "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900",
          "dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white",
          "shadow-sm hover:shadow-md"
        ],
        secondary: [
          "bg-slate-100 text-slate-900 hover:bg-slate-200 focus:bg-slate-200",
          "dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 dark:focus:bg-slate-600",
          "shadow-sm hover:shadow-md",
          "border border-transparent"
        ],
        ghost: [
          "text-slate-700 hover:bg-slate-100 hover:text-slate-900 focus:bg-slate-100",
          "dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white dark:focus:bg-slate-800",
          "border border-transparent"
        ],
        link: [
          "text-slate-900 underline-offset-4 hover:underline",
          "dark:text-white",
          "h-auto p-0"
        ],
        success: [
          "bg-green-500 !text-white hover:bg-green-600",
          "shadow-sm hover:shadow-md"
        ],
        warning: [
          "bg-yellow-500 !text-white hover:bg-yellow-600",
          "shadow-sm hover:shadow-md"
        ],
        error: [
          "bg-red-500 !text-white hover:bg-red-600",
          "shadow-sm hover:shadow-md"
        ],
        brand: [
          "bg-brand-600 !text-white hover:bg-brand-700",
          "shadow-sm hover:shadow-md"
        ],
        black: [
          "bg-black !text-white hover:bg-slate-900 focus:bg-slate-900",
          "dark:bg-white dark:!text-black dark:hover:bg-slate-100 dark:focus:bg-slate-100",
          "shadow-sm hover:shadow-md",
          "border border-transparent"
        ]
      },
      size: {
        xs: "h-8 px-3 text-xs rounded-md",
        sm: "h-9 px-4 text-caption rounded-md",
        default: "h-10 px-6 text-body-small",
        lg: "h-12 px-8 text-body",
        xl: "h-14 px-10 text-body-large",
        icon: "h-10 w-10 p-0",
        "icon-sm": "h-8 w-8 p-0",
        "icon-lg": "h-12 w-12 p-0"
      }
    },
    defaultVariants: {
      variant: "black",
      size: "default"
    }
  }
)

export type ButtonVariants = VariantProps<typeof buttonVariants>

/* ========================================
   BADGE VARIANTS

   Premium color palette based on Agion brand:
   - Brand amber: #fad780, #f5af01, #ea8a00
   - Logo text: #0a141e (dark navy)

   Design principles:
   - Soft, muted backgrounds for elegance
   - Clear semantic meaning
   - Warm undertones for cohesion
   - WCAG AA compliant contrast
   ======================================== */

export const badgeVariants = cva(
  [
    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
    "transition-colors"
  ],
  {
    variants: {
      variant: {
        // Solid dark - primary emphasis
        default: [
          "bg-slate-900 text-white",
          "dark:bg-slate-100 dark:text-slate-900"
        ],
        // Subtle neutral - secondary info
        secondary: [
          "bg-slate-100 text-slate-700 border border-slate-200/60",
          "dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700/40"
        ],
        // Muted - de-emphasized
        muted: [
          "bg-slate-50 text-slate-500 border border-slate-100",
          "dark:bg-slate-800/40 dark:text-slate-400 dark:border-slate-700/30"
        ],
        // Outline - minimal
        outline: [
          "bg-transparent text-slate-700 border border-slate-300",
          "dark:text-slate-300 dark:border-slate-600"
        ],
        // Success - emerald tones (warm green)
        success: [
          "bg-emerald-50 text-emerald-800 border border-emerald-200/70",
          "dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800/50"
        ],
        // Warning - amber tones (brand color)
        warning: [
          "bg-amber-50 text-amber-800 border border-amber-200/70",
          "dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50"
        ],
        // Error/Destructive - rose tones (warm red)
        error: [
          "bg-rose-50 text-rose-800 border border-rose-200/70",
          "dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800/50"
        ],
        destructive: [
          "bg-rose-50 text-rose-800 border border-rose-200/70",
          "dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800/50"
        ],
        // Info - sky tones (sophisticated blue)
        info: [
          "bg-sky-50 text-sky-800 border border-sky-200/70",
          "dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-800/50"
        ],
        // Brand - amber/gold (Agion brand color)
        brand: [
          "bg-amber-100 text-amber-900 border border-amber-300/70",
          "dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-700/50"
        ],
        // Premium - special status (teal)
        premium: [
          "bg-teal-50 text-teal-800 border border-teal-200/70",
          "dark:bg-teal-950/50 dark:text-teal-300 dark:border-teal-800/50"
        ],
        // Elevated - high trust tier (deep green, darker than success)
        elevated: [
          "bg-green-100 text-green-900 border border-green-300/80",
          "dark:bg-green-950/60 dark:text-green-200 dark:border-green-700/60"
        ],
        // Purple - admin/special roles
        purple: [
          "bg-violet-50 text-violet-800 border border-violet-200/70",
          "dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-800/50"
        ]
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        default: "px-2.5 py-0.5 text-xs",
        lg: "px-3 py-1 text-sm"
      }
    },
    defaultVariants: {
      variant: "secondary",
      size: "default"
    }
  }
)

export type BadgeVariants = VariantProps<typeof badgeVariants>

/* ========================================
   CARD VARIANTS
   ======================================== */

export const cardVariants = cva(
  [
    "rounded-xl border bg-card text-foreground shadow-card",
    "dark:border-slate-700 dark:bg-slate-900 dark:text-white"
  ],
  {
    variants: {
      variant: {
        default: "border-slate-200 dark:border-slate-700",
        elevated: "shadow-md border-slate-200 dark:border-slate-700",
        outlined: "border-2 border-slate-200 shadow-none dark:border-slate-700",
        ghost: "border-transparent shadow-none bg-transparent"
      },
      padding: {
        none: "",
        xs: "p-3",
        sm: "p-4",
        default: "p-6",
        lg: "p-8",
        xl: "p-10"
      },
      hover: {
        none: "",
        lift: "card-hover",
        glow: "hover:shadow-lg hover:shadow-brand-500/20 transition-smooth",
        interactive: "interactive cursor-pointer"
      }
    },
    defaultVariants: {
      variant: "default",
      padding: "default",
      hover: "none"
    }
  }
)

export type CardVariants = VariantProps<typeof cardVariants>

/* ========================================
   INPUT VARIANTS
   ======================================== */

export const inputVariants = cva(
  [
    "flex w-full rounded-lg border border-border bg-background px-3",
    "text-sm placeholder:text-muted-foreground",
    "focus-ring",
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-50",
    "dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-400",
    "transition-smooth"
  ],
  {
    variants: {
      variant: {
        default: "border-slate-200 dark:border-slate-700",
        error: "border-red-500 focus-visible:ring-red-500",
        success: "border-green-500 focus-visible:ring-green-500"
      },
      size: {
        sm: "h-8 px-3 text-xs rounded-md",
        default: "h-10 px-3 text-sm",
        lg: "h-12 px-4 text-base"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

export type InputVariants = VariantProps<typeof inputVariants>

/* ========================================
   ALERT VARIANTS
   ======================================== */

export const alertVariants = cva(
  [
    "relative w-full rounded-lg border p-4",
    "[&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4"
  ],
  {
    variants: {
      variant: {
        default: "bg-white text-slate-900 border-slate-200 dark:bg-slate-900 dark:text-white dark:border-slate-700",
        destructive: [
          "border-red-500 text-red-900 bg-red-50",
          "dark:border-red-500 dark:text-red-100 dark:bg-red-950",
          "[&>svg]:text-red-600"
        ],
        success: [
          "border-green-500 text-green-900 bg-green-50",
          "dark:border-green-500 dark:text-green-100 dark:bg-green-950",
          "[&>svg]:text-green-600"
        ],
        warning: [
          "border-yellow-500 text-yellow-900 bg-yellow-50",
          "dark:border-yellow-500 dark:text-yellow-100 dark:bg-yellow-950",
          "[&>svg]:text-yellow-600"
        ],
        info: [
          "border-blue-500 text-blue-900 bg-blue-50",
          "dark:border-blue-500 dark:text-blue-100 dark:bg-blue-950",
          "[&>svg]:text-blue-600"
        ]
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
)

export type AlertVariants = VariantProps<typeof alertVariants>

/* ========================================
   STATUS VARIANTS

   Operational status indicators with consistent
   premium styling matching the badge system.
   ======================================== */

export const statusVariants = cva(
  [
    "inline-flex items-center gap-2 rounded-full text-xs font-medium"
  ],
  {
    variants: {
      status: {
        online: [
          "bg-emerald-50 text-emerald-800 border border-emerald-200/70",
          "dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800/50"
        ],
        offline: [
          "bg-slate-100 text-slate-600 border border-slate-200/60",
          "dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/40"
        ],
        warning: [
          "bg-amber-50 text-amber-800 border border-amber-200/70",
          "dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50"
        ],
        error: [
          "bg-rose-50 text-rose-800 border border-rose-200/70",
          "dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800/50"
        ],
        maintenance: [
          "bg-sky-50 text-sky-800 border border-sky-200/70",
          "dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-800/50"
        ],
        pending: [
          "bg-amber-50 text-amber-800 border border-amber-200/70",
          "dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50"
        ],
        inactive: [
          "bg-slate-50 text-slate-500 border border-slate-100",
          "dark:bg-slate-800/40 dark:text-slate-400 dark:border-slate-700/30"
        ]
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        default: "px-2.5 py-0.5 text-xs",
        lg: "px-3 py-1 text-sm"
      }
    },
    defaultVariants: {
      status: "offline",
      size: "default"
    }
  }
)

export type StatusVariants = VariantProps<typeof statusVariants>

/* ========================================
   LOADING SPINNER VARIANTS
   ======================================== */

export const spinnerVariants = cva(
  [
    "animate-spin rounded-full border-2 border-slate-300 border-t-slate-900",
    "dark:border-slate-600 dark:border-t-white"
  ],
  {
    variants: {
      size: {
        sm: "h-4 w-4",
        default: "h-6 w-6",
        lg: "h-8 w-8",
        xl: "h-12 w-12"
      },
      variant: {
        default: "border-slate-300 border-t-slate-900 dark:border-slate-600 dark:border-t-white",
        muted: "border-slate-200 border-t-slate-500 dark:border-slate-700 dark:border-t-slate-400",
        white: "border-white/30 border-t-white"
      }
    },
    defaultVariants: {
      size: "default",
      variant: "default"
    }
  }
)

export type SpinnerVariants = VariantProps<typeof spinnerVariants>

/* ========================================
   NAVIGATION VARIANTS
   ======================================== */

export const navLinkVariants = cva(
  [
    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
    "transition-all duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
  ],
  {
    variants: {
      variant: {
        default: [
          "text-slate-600 hover:text-slate-900 hover:bg-slate-100",
          "dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
        ],
        ghost: [
          "text-slate-600 hover:text-slate-900",
          "dark:text-slate-400 dark:hover:text-white",
          "hover:bg-transparent"
        ]
      },
      active: {
        true: "bg-brand-600 text-white hover:bg-brand-700",
        false: ""
      },
      collapsed: {
        true: "justify-center px-2",
        false: ""
      }
    },
    compoundVariants: [
      {
        active: true,
        variant: "default",
        className: "hover:bg-brand-700 hover:text-white"
      }
    ],
    defaultVariants: {
      variant: "default",
      active: false,
      collapsed: false
    }
  }
)

export type NavLinkVariants = VariantProps<typeof navLinkVariants>

/* ========================================
   TEXT LINK VARIANTS
   ======================================== */

export const textLinkVariants = cva(
  [
    "inline-flex items-center gap-1 font-medium",
    "transition-colors duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 dark:focus-visible:ring-white focus-visible:ring-offset-2"
  ],
  {
    variants: {
      variant: {
        default: [
          "text-slate-900 hover:text-slate-700 underline-offset-4 hover:underline",
          "dark:text-white dark:hover:text-slate-300"
        ],
        muted: [
          "text-slate-600 hover:text-slate-900 underline-offset-4 hover:underline",
          "dark:text-slate-400 dark:hover:text-white"
        ],
        subtle: [
          "text-slate-600 hover:text-slate-900",
          "dark:text-slate-400 dark:hover:text-white"
        ]
      },
      size: {
        sm: "text-xs",
        default: "text-sm",
        lg: "text-base"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
)

export type TextLinkVariants = VariantProps<typeof textLinkVariants>

/* ========================================
   UTILITY TYPES
   ======================================== */

// Utility type for className props
export type ClassNameProp = {
  className?: string
}

// Utility type for variant props
export type WithVariants<T> = T & ClassNameProp